import { useEffect, useRef } from 'react'
import usePlanMetrics from './usePlanMetrics'
import { toGlobalParams } from './planMetricsParams'

// Contract §1 / RN3 / RN25 -- the same global filters (and `timeZone` only if the ribbon ever sends it) as
// the indicators ribbon; the detail is always weekly and read by planned date (see `planMetricsParams`).

/**
 * Story 175 (CU5, contract §1/§7) -- the machine detail read, built on `usePlanMetrics` so it inherits
 * the 400 ms debounce and the "only the latest answer paints" discard (RN53/P4) instead of
 * re-implementing them.
 *
 * - RN52: nothing is requested while the drawer is closed (`deviceId` null) or without a plan.
 * - RN51/RN8: `metricsVersion` is the Planner's existing "the plan changed" signal (Gantt drop,
 *   publish, reset, save, activate, plan, delete, search); a bump re-reads the SAME machine.
 * - `data` is only handed out while it describes the machine being asked for, so switching machines
 *   (RN7) never paints the previous machine under the new one's request.
 */
const useMachineDetail = ({ planificationId, deviceId, plannerFilters, metricsVersion = 0, enabled = true }) => {
  const isOpen = deviceId !== null && deviceId !== undefined
  const { data, loading, error, reload } = usePlanMetrics({
    planificationId,
    endpoint: `devices/${deviceId}`,
    params: toGlobalParams(plannerFilters),
    enabled: enabled && isOpen
  })

  const isFirstMetricsVersionRender = useRef(true)
  useEffect(() => {
    if (isFirstMetricsVersionRender.current) {
      isFirstMetricsVersionRender.current = false
      return
    }
    reload()
  }, [metricsVersion, reload])

  const describesRequestedMachine = Boolean(data && data.device && data.device.deviceId === deviceId)
  return { data: describesRequestedMachine ? data : null, loading, error, reload }
}

export default useMachineDetail
